import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, StarIcon } from 'lucide-react';
import { Reveal } from '../ui/Reveal';
import { SectionLabel } from '../ui/SectionLabel';
import { SplitText } from '../ui/SplitText';
import { testimonials } from '../../data/testimonials';
import { EASE_OUT } from '../../utils/motion';
const AUTOPLAY_MS = 7000;
export function TestimonialsSection() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const current = testimonials[index];
  const go = (dir: number) => setIndex(i => (i + dir + testimonials.length) % testimonials.length);
  useEffect(() => {
    if (paused) return;
    const id = window.setTimeout(() => go(1), AUTOPLAY_MS);
    return () => window.clearTimeout(id);
  }, [index, paused]);
  const initials = current.name.split(' ').map(n => n[0]).join('');
  return <section className="bg-mist py-24 lg:py-36" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} aria-roledescription="carrousel" aria-label="Témoignages clients" data-id="element-273" data-mp="3470m8dg8c4dy">
      <div className="mx-auto grid max-w-[1440px] gap-14 px-5 sm:px-8 lg:grid-cols-12" data-id="element-274" data-mp="1fac2guwr85cy">
        <div className="lg:col-span-4" data-id="element-275" data-mp="1ajvnv7h76dp0">
          <Reveal data-id="element-276" data-mp="1aiu84pp8l30y">
            <SectionLabel data-id="element-277" data-mp="1aiapf6rrialv">Témoignages</SectionLabel>
          </Reveal>
          <h2 className="mt-6 text-[clamp(2rem,4vw,3.25rem)] font-medium leading-[1.08] tracking-[-0.03em]" data-id="element-278" data-mp="1oa8un6icrmcr">
            <SplitText text="Ils m’ont confié leurs achats." data-id="element-279" data-mp="1k8x14pwcygza" />
          </h2>
          <Reveal delay={0.15} data-id="element-280" data-mp="1y9zmt1jfxp9o">
            <div className="mt-8 flex items-center gap-3" data-id="element-281" data-mp="1y8y2e6gjbqfa">
              <div className="flex gap-0.5 text-brass-dark" aria-hidden="true" data-id="element-282" data-mp="1t9v9fkdtxano">
                {[0, 1, 2, 3, 4].map(s => <StarIcon key={s} className="h-4 w-4 fill-current" data-id="element-283" data-mp="3w899qxzql8y" />)}
              </div>
              <p className="text-sm text-muted" data-id="element-284" data-mp="3v3ayicu9qhgj">4,9/5 de satisfaction client moyenne</p>
            </div>
            <div className="mt-8 flex gap-2" data-id="element-285" data-mp="1z06c5bm0mrdd">
              <button type="button" onClick={() => go(-1)} aria-label="Témoignage précédent" className="grid h-12 w-12 place-items-center rounded-xl bg-white text-ink transition-colors duration-150 hover:bg-ink hover:text-white" data-id="element-286" data-mp="23xeqt27ygd9v">
                <ArrowLeftIcon className="h-5 w-5" data-id="element-287" data-mp="2hxz97x9yk72u" />
              </button>
              <button type="button" onClick={() => go(1)} aria-label="Témoignage suivant" className="grid h-12 w-12 place-items-center rounded-xl bg-ink text-white transition-colors duration-150 hover:bg-ink-700" data-id="element-288" data-mp="2mwkc64glh4uz">
                <ArrowRightIcon className="h-5 w-5" data-id="element-289" data-mp="2vqp2mmrtge9z" />
              </button>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-8" data-id="element-290" data-mp="35milsp01j60q">
          <figure className="min-h-[300px] sm:min-h-[260px]" aria-live="polite" data-id="element-291" data-mp="35njtqklrfce8">
            <AnimatePresence mode="wait" data-id="element-292" data-mp="3frrxhoaxxs7w">
              <motion.div key={index} initial={{
              opacity: 0,
              y: 14
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.25,
              ease: EASE_OUT
            }} data-id="element-293" data-mp="3fom96ba4yud8">
                <blockquote className="text-[clamp(1.4rem,2.6vw,2.15rem)] font-medium leading-[1.3] tracking-[-0.015em] text-ink" data-id="element-294" data-mp="31t0m49ngqkng">
                  « {current.quote} »
                </blockquote>
                <figcaption className="mt-10 flex items-center gap-4" data-id="element-295" data-mp="3tj5fgxt7ai0t">
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-ink text-sm font-medium text-white" aria-hidden="true" data-id="element-296" data-mp="3oqlq73l5hyg6">
                    {initials}
                  </span>
                  <span data-id="element-297" data-mp="l3lpq8rqnhqi">
                    <span className="block font-medium" data-id="element-298" data-mp="l44zv3b5d1tz">{current.name}</span>
                    <span className="block text-sm text-muted" data-id="element-299" data-mp="gasj1fcplzbs">
                      {current.role} · {current.company}
                    </span>
                  </span>
                </figcaption>
              </motion.div>
            </AnimatePresence>
          </figure>

          <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-4" role="tablist" aria-label="Choisir un témoignage" data-id="element-300" data-mp="3nz1qrklfq6ud">
            {testimonials.map((t, i) => <button key={t.name} type="button" role="tab" aria-selected={i === index} onClick={() => setIndex(i)} className="text-left" data-id="element-301" data-mp="3s27w8q0q3tzs" data-mpi={i}>
                <span className="relative block h-[2px] overflow-hidden rounded-full bg-ink/15" data-id="element-302" data-mp="aqyo80t40fgb">
                  {i === index && <motion.span key={`${index}-${paused}`} initial={{
                scaleX: 0
              }} animate={{
                scaleX: 1
              }} transition={{
                duration: paused ? 0 : AUTOPLAY_MS / 1000,
                ease: 'linear'
              }} className="absolute inset-0 origin-left bg-ink" data-id="element-303" data-mp="ers8mk7cfc4g" />}
                </span>
                <span className={`mt-3 block truncate text-sm transition-colors duration-150 ${i === index ? 'text-ink' : 'text-muted'}`} data-id="element-304" data-mp="2mz5momchgril">
                  {t.company}
                </span>
              </button>)}
          </div>
        </div>
      </div>
    </section>;
}